// Labor guide: the shop's book hours per repair. Billed hours = book hours, however long the job really took.
import { useState } from 'react';
import { api, useApi, hours, toHundredths, type Row } from '../lib';
import { Badge, Button, Panel, Table, Loading, ErrorBox, Modal, Form, Field, Select, TextArea, SearchBox, useToast, Icon, confirmAction } from '../ui';

export const APPLIES_LABEL: Record<string, string> = { any: 'Truck & trailer', truck: 'Truck', trailer: 'Trailer' };

/** Inline "book hours" box: type and press Enter (or leave) to save. */
function HoursEdit({ op, onSaved }: { op: Row; onSaved: () => void }) {
  const [val, setVal] = useState((op.hours / 100).toString());
  const toast = useToast();
  const save = async () => {
    const h = toHundredths(val);
    if (h === op.hours) return;
    try { await api(`/api/labor-guide/${op.id}`, { method: 'PATCH', body: { hours: h } }); onSaved(); } catch (e) { toast((e as Error).message, 'err'); }
  };
  return (
    <span className="sup-in hrs-in"><input inputMode="decimal" aria-label={`Book hours for ${op.name}`} value={val} onChange={e => setVal(e.target.value)} onFocus={e => e.target.select()}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} onBlur={() => void save()} /> h</span>
  );
}

export function LaborGuide({ office = true }: { office?: boolean }) {
  const { data, error, reload } = useApi<Row[]>('/api/labor-guide');
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [onlyUnchecked, setOnlyUnchecked] = useState(false);
  const [edit, setEdit] = useState<null | 'new' | Row>(null);
  const toast = useToast();
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const cats = [...new Set(data.map(o => o.category as string))];
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const list = data.filter(o => (!cat || o.category === cat) && (!onlyUnchecked || !o.checked) && words.every(w => `${o.name} ${o.category} ${o.note}`.toLowerCase().includes(w)));
  const unchecked = data.filter(o => !o.checked).length;
  const toggle = async (o: Row) => { await api(`/api/labor-guide/${o.id}`, { method: 'PATCH', body: { checked: !o.checked } }); reload(); };

  return (
    <>
      <div className="page-head">
        <div><h1>Labor guide</h1><p>Book hours for each repair — this is what the customer pays, whether the job took 3 hours or 10.</p></div>
        {office && <div className="actions"><Button kind="primary" icon="plus" onClick={() => setEdit('new')}>Add repair</Button></div>}
      </div>
      {unchecked > 0 && <div className="hint-bar">
        <Icon name="alert" size={16} /> <span><b>{unchecked} repairs still have Asena Bay starting hours</b> — rough estimates to begin with. Type your shop's book hours (it's marked <Badge tone="green">Checked</Badge> when you change it) or tick Checked if the number is right.</span>
      </div>}
      <div className="toolbar">
        <SearchBox value={q} onChange={setQ} placeholder="Search: kingpin, brake chamber, EGR…" />
        <select value={cat} onChange={e => setCat(e.target.value)} aria-label="Category"><option value="">All categories</option>{cats.map(c => <option key={c}>{c}</option>)}</select>
        <label className="check inline"><input type="checkbox" checked={onlyUnchecked} onChange={e => setOnlyUnchecked(e.target.checked)} /> Not checked yet</label>
      </div>
      <Panel>
        <Table heads={['Repair', 'Category', 'For', 'Book hours', 'Your history', '']} empty="Nothing matches."
          rows={list.map(o => [
            <><b>{o.name}</b>{o.note && <small className="muted">{o.note}</small>}</>,
            <small>{o.category}</small>,
            <small>{APPLIES_LABEL[o.applies] || o.applies}</small>,
            office ? <span className="guide-hrs"><HoursEdit key={o.id + o.hours} op={o} onSaved={reload} />
              <button className={'chk-btn' + (o.checked ? ' on' : '')} title={o.checked ? 'Checked by the shop' : 'Starting estimate — click when these hours are right'} onClick={() => void toggle(o)}>{o.checked ? '✓ Checked' : 'Check'}</button></span>
              : <b>{hours(o.hours)} h</b>,
            o.used ? <small>Billed {o.used}× · avg {hours(o.avg_hours)} h</small> : <small className="muted">—</small>,
            office && <span className="row-actions">
              <button className="icon-btn" title="Edit" onClick={() => setEdit(o)}><Icon name="edit" size={16} /></button>
              <button className="icon-btn" title="Remove from guide" onClick={async () => { if (confirmAction(`Remove "${o.name}" from the labor guide?`)) { await api(`/api/labor-guide/${o.id}`, { method: 'DELETE' }); toast('Removed'); reload(); } }}><Icon name="trash" size={16} /></button>
            </span>,
          ])} />
      </Panel>
      {edit && <GuideModal op={edit === 'new' ? undefined : edit} cats={cats} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </>
  );
}

export function GuideModal({ op, cats, name, hoursDefault, onClose, onSaved }: { op?: Row; cats: string[]; name?: string; hoursDefault?: number; onClose: () => void; onSaved: (id?: string) => void }) {
  return (
    <Modal title={op ? 'Edit repair' : 'Add repair to labor guide'} onClose={onClose}>
      <Form onCancel={onClose} onSubmit={async v => {
        const body = { name: v.name, category: v.category_new?.trim() || v.category, applies: v.applies, hours: toHundredths(v.hours), note: v.note };
        if (op) { await api(`/api/labor-guide/${op.id}`, { method: 'PATCH', body }); onSaved(op.id); }
        else onSaved((await api<{ id: string }>('/api/labor-guide', { body })).id);
      }}>
        <Field label="Repair" name="name" required full defaultValue={op?.name ?? name} placeholder="e.g. Kingpin replacement — both sides" />
        <Field label="Book hours" name="hours" type="number" step="0.1" min="0" required defaultValue={op ? String(op.hours / 100) : hoursDefault ? String(hoursDefault / 100) : ''} hint="What the customer is billed" />
        <Select label="For" name="applies" defaultValue={op?.applies || 'any'}><option value="any">Truck & trailer</option><option value="truck">Truck</option><option value="trailer">Trailer</option></Select>
        <Select label="Category" name="category" defaultValue={op?.category || cats[0] || 'Other'}>{[...new Set([...cats, 'Other'])].map(c => <option key={c}>{c}</option>)}</Select>
        <Field label="…or a new category" name="category_new" placeholder="optional" />
        <TextArea label="Note (optional)" name="note" defaultValue={op?.note} />
      </Form>
    </Modal>
  );
}
